import { useCallback, useMemo } from 'react';
import { type ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import {
  ACTION_CODE_KEY,
  ACTION_SUBTYPE_KEY,
  ACTION_AUTH_KEY,
  ACTION_AUTH_START_KEY,
  ACTION_AUTH_END_KEY,
  ACTION_REFER_SELF_KEY,
  ACTION_SPECIALITY_KEY,
  ACTION_SUBSPECIALITY_KEY,
  ACTION_REASON_KEY,
  ACTION_COMMENT_KEY,
  ACTION_CALL_TYPE_KEY,
  ACTION_OTHER_REASON_KEY,
  ACTION_URGENCY_KEY,
  ACTION_REFCLI_KEY,
  ACTION_POSTCODE_KEY,
  ACTION_DISCHARGE_REASON_KEY
} from '@components/Clinicians/session-form';
import { useSessionFormState } from './useSessionFormState';
import { useSessionOutcome } from './useSessionOutcome';
import { useSessionAppointmentId } from './useSessionAppointmentId';
import useAppointmentDetails from './appointments/useAppointmentDetails';

export const useSessionAction = <A extends ApptActionCodes>(appointmentActionCode: A) => {
  const { outcome } = useSessionOutcome();

  const apptId = useSessionAppointmentId();
  const { detailsCurrentAppointment } = useAppointmentDetails(apptId);

  const ACTION_KEY = `${ACTION_CODE_KEY}_${appointmentActionCode}` as const;
  const SUBTYPE_KEY = `${ACTION_SUBTYPE_KEY}_${appointmentActionCode}` as const;
  const REFER_SELF_KEY = `${ACTION_REFER_SELF_KEY}_${appointmentActionCode}` as const;
  const SPECIALITY_KEY = `${ACTION_SPECIALITY_KEY}_${appointmentActionCode}` as const;
  const SUBSPECIALITY_KEY = `${ACTION_SUBSPECIALITY_KEY}_${appointmentActionCode}` as const;
  const REASON_KEY = `${ACTION_REASON_KEY}_${appointmentActionCode}` as const;
  const COMMENT_KEY = `${ACTION_COMMENT_KEY}_${appointmentActionCode}` as const;
  const CALL_TYPE_KEY = `${ACTION_CALL_TYPE_KEY}_${appointmentActionCode}` as const;
  const AUTH_KEY = `${ACTION_AUTH_KEY}_${appointmentActionCode}` as const;
  const AUTH_START_KEY = `${ACTION_AUTH_START_KEY}_${appointmentActionCode}` as const;
  const AUTH_END_KEY = `${ACTION_AUTH_END_KEY}_${appointmentActionCode}` as const;
  const OTHER_REASON_KEY = `${ACTION_OTHER_REASON_KEY}_${appointmentActionCode}` as const;
  const URGENCY_KEY = `${ACTION_URGENCY_KEY}_${appointmentActionCode}` as const;
  const REFCLI_KEY = `${ACTION_REFCLI_KEY}_${appointmentActionCode}` as const;
  const POSTCODE_KEY = `${ACTION_POSTCODE_KEY}_${appointmentActionCode}` as const;
  const DISCHARGE_REASON_KEY = `${ACTION_DISCHARGE_REASON_KEY}_${appointmentActionCode}` as const;

  const [appointmentActions = [], setAppointmentActions] = useSessionFormState('appt_actions');

  const initialActionSubtype =
    detailsCurrentAppointment?.appt_actions?.find((a) => a.appt_action_code === appointmentActionCode)
      ?.action_subtype_code ?? undefined;

  const [selectedSubType] = useSessionFormState(SUBTYPE_KEY, initialActionSubtype);

  const { data: appointmentsMeta } = useAppointmentMeta();

  const meta = useMemo(
    () =>
      appointmentsMeta?.appt_version === 1
        ? undefined
        : appointmentsMeta?.appt_outcomes
            ?.find((item) => item?.appt_outcome_code === outcome)
            ?.appt_actions?.find((action) => action.appt_action_code === appointmentActionCode),
    [appointmentsMeta?.appt_version, appointmentsMeta?.appt_outcomes, outcome, appointmentActionCode]
  );

  const selectedSubTypeMeta = useMemo(
    () => meta?.action_subtypes?.find(({ action_subtype_code }) => action_subtype_code === selectedSubType),
    [meta?.action_subtypes, selectedSubType]
  );

  const isSelected = appointmentActions?.some((action) => action.appt_action_code === appointmentActionCode);

  const toggle = useCallback(
    (value: boolean) => {
      if (!appointmentActionCode) return;

      setAppointmentActions((prev) =>
        value
          ? [{ appt_action_code: appointmentActionCode }].concat((prev as { appt_action_code: A }[]) || [])
          : prev?.filter((action) => action.appt_action_code !== appointmentActionCode)
      );
    },
    [appointmentActionCode, setAppointmentActions]
  );

  return {
    selectedSubType,
    selectedSubTypeMeta,
    ACTION_KEY,
    SUBTYPE_KEY,
    AUTH_KEY,
    AUTH_START_KEY,
    AUTH_END_KEY,
    REFER_SELF_KEY,
    SPECIALITY_KEY,
    SUBSPECIALITY_KEY,
    REASON_KEY,
    COMMENT_KEY,
    CALL_TYPE_KEY,
    OTHER_REASON_KEY,
    URGENCY_KEY,
    REFCLI_KEY,
    POSTCODE_KEY,
    DISCHARGE_REASON_KEY,
    meta,
    isSelected,
    toggle
  };
};
